import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFilter,
  faEye,
  faEdit,
  faTrash,
  faPlus,
  faTimes,
  faLink,
  faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import './AbseilSystems.css';
import { toast } from 'react-toastify';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';

const ABSEIL_SYSTEM_TYPES = [
  'Davit Arm',
  'Davit Base',
  'Parapet Clamp',
  'Rail System',
  'Track System',
  'Anchor Plate',
  'Post-Type Anchor',
  'Monorail System',
  'Fixed Boom',
  'Rolling Davit',
  'Mobile Davit',
  'Bosun\'s Chair Anchor',
  'Custom Abseil Point'
];

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = {
    pass: 'rs-badge-pass',
    passed: 'rs-badge-passed',
    fail: 'rs-badge-fail',
    failed: 'rs-badge-failed',
    pending: 'rs-badge-pending',
    overdue: 'rs-badge-overdue',
    unknown: 'rs-badge-unknown'
  };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
  } catch {
    return 'N/A';
  }
};

const calculateNextTestDate = (lastTestDate) => {
  if (!lastTestDate) return '';
  const date = new Date(lastTestDate);
  if (isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + 6);
  return date.toISOString().split('T')[0];
};

export default function AbseilSystems({ propertyId }) {
  const [abseilSystems, setAbseilSystems] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [detailAsset, setDetailAsset] = useState(null);
  const [newSystem, setNewSystem] = useState({
    systemName: '',
    location: '',
    type: ABSEIL_SYSTEM_TYPES[0],
    installedBy: '',
    dateInstalled: new Date().toISOString().split('T')[0],
    lastTestDate: new Date().toISOString().split('T')[0],
    status: 'Unknown',
    certificateUrl: '',
    notes: ''
  });

  const fetchAbseilSystemsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/abseil-systems`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch abseil systems: ${response.status}`);

      const data = await response.json();
      const systemsArray = Array.isArray(data) ? data : [data];

      const formattedData = systemsArray.map(system => ({
        id: system.id,
        propertyId: system.property_id,
        systemName: system.system_name || `Abseil System ${system.id}`,
        location: system.location_description || system.location || 'N/A',
        type: system.type || 'Davit Arm',
        installedBy: system.installed_by || 'N/A',
        dateInstalled: formatDate(system.date_installed),
        lastTestDate: formatDate(system.last_inspection_date),
        nextTestDate: system.next_inspection_date
          ? formatDate(system.next_inspection_date)
          : (system.last_inspection_date ? formatDate(calculateNextTestDate(system.last_inspection_date)) : 'N/A'),
        status: system.status || 'Unknown',
        capacity: system.capacity ? `${system.capacity}kN` : 'N/A',
        certificateUrl: system.certificate_url,
        notes: system.notes || ''
      }));

      setAbseilSystems(formattedData);
    } catch (err) {
      console.error('Error in fetchAbseilSystemsData:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to fetch abseil systems');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSystem = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const requestData = {
        system_name: newSystem.systemName,
        location_description: newSystem.location,
        type: newSystem.type,
        installed_by: newSystem.installedBy,
        date_installed: newSystem.dateInstalled,
        last_inspection_date: newSystem.lastTestDate,
        status: 'Unknown',
        certificate_url: newSystem.certificateUrl,
        notes: newSystem.notes
      };

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/abseil-systems`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) throw new Error('Failed to create abseil system');

      setShowCreateModal(false);
      setNewSystem({
        systemName: '',
        location: '',
        type: ABSEIL_SYSTEM_TYPES[0],
        installedBy: '',
        dateInstalled: new Date().toISOString().split('T')[0],
        lastTestDate: new Date().toISOString().split('T')[0],
        status: 'Unknown',
        certificateUrl: '',
        notes: ''
      });
      toast.success('Abseil system created successfully');
      fetchAbseilSystemsData();
    } catch (err) {
      console.error('Error creating abseil system:', err);
      toast.error(err.message || 'Failed to create abseil system');
    }
  };

  const handleEditSystem = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/abseil-systems/${editingSystem.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          system_name: editingSystem.systemName,
          location_description: editingSystem.location,
          type: editingSystem.type,
          installed_by: editingSystem.installedBy,
          date_installed: editingSystem.dateInstalled,
          last_inspection_date: editingSystem.lastTestDate,
          status: editingSystem.status,
          certificate_url: editingSystem.certificateUrl,
          notes: editingSystem.notes
        })
      });

      if (!response.ok) throw new Error('Failed to update abseil system');

      setShowEditModal(false);
      setEditingSystem(null);
      toast.success('Abseil system updated successfully');
      fetchAbseilSystemsData();
    } catch (err) {
      console.error('Error updating abseil system:', err);
      toast.error(err.message || 'Failed to update abseil system');
    }
  };

  const handleDeleteSystem = async (systemId) => {
    if (!window.confirm('Are you sure you want to delete this abseil system?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/abseil-systems/${systemId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete abseil system');

      setAbseilSystems(prev => prev.filter(s => s.id !== systemId));
      setShowEditModal(false);
      setEditingSystem(null);
      toast.success('Abseil system deleted successfully');
    } catch (err) {
      console.error('Error deleting abseil system:', err);
      toast.error(err.message || 'Failed to delete abseil system');
    }
  };

  const handleViewTestData = (system) => {
    openLatestTestReport({
      propertyId,
      assetType: 'abseil_system',
      assetId: system.id,
      fallbackAsset: system,
      emptyMessage: 'No test data available for this system',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  useEffect(() => {
    if (propertyId) fetchAbseilSystemsData();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'davit_bases' && assetId && abseilSystems.length) {
        const asset = abseilSystems.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [abseilSystems]);

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  // Filtered data
  const filtered = abseilSystems
    .filter(s => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [s.systemName, s.location, s.type, s.status, s.installedBy].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(s => typeFilter === 'All' || s.type === typeFilter);

  // ─── Error State ───
  if (error) {
    return (
      <div className="abseil-systems-section">
        <h1>Abseil Systems</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faLink} />
          <span>{error}</span>
          <button onClick={fetchAbseilSystemsData}>Retry</button>
        </div>
      </div>
    );
  }

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="abseil-systems-section">
        <h1>Abseil Systems</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="abseil-systems-section">
      <h1>Abseil Systems</h1>

      {/* Toolbar */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search abseil systems..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table / Cards */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>System Name</th>
                <th>Location</th>
                <th>Type</th>
                <th>Capacity</th>
                <th>Installed By</th>
                <th>Date Installed</th>
                <th>Last Test</th>
                <th>Next Test</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="11">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faLink} /></div>
                      <h3 className="rs-empty-title">
                        {abseilSystems.length === 0 ? 'No abseil systems yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {abseilSystems.length === 0
                          ? 'Add your first abseil system to start tracking safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(system => (
                <tr key={system.id}>
                  <td title={system.id}>{system.id}</td>
                  <td title={system.systemName}>{system.systemName}</td>
                  <td title={system.location}>{system.location}</td>
                  <td title={system.type}>{system.type}</td>
                  <td title={system.capacity}>{system.capacity}</td>
                  <td title={system.installedBy}>{system.installedBy}</td>
                  <td>{system.dateInstalled}</td>
                  <td>{system.lastTestDate}</td>
                  <td>{system.nextTestDate}</td>
                  <td>
                    <span className={getBadgeClass(system.status)}>
                      {system.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(system)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(system)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (
                        <>
                          <button className="rs-action-btn" onClick={() => { setEditingSystem(system); setShowEditModal(true); }} title="Edit" aria-label="Edit abseil system">
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteSystem(system.id)} title="Delete" aria-label="Delete abseil system">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faLink} /></div>
              <h3 className="rs-empty-title">
                {abseilSystems.length === 0 ? 'No abseil systems yet' : 'No results found'}
              </h3>
              <p className="rs-empty-desc">
                {abseilSystems.length === 0
                  ? 'Add your first abseil system to start tracking.'
                  : 'Adjust your search or filters.'}
              </p>
            </div>
          ) : filtered.map(system => (
            <div key={system.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{system.systemName}</span>
                <span className={getBadgeClass(system.status)}>{system.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Location</span>
                  <span className="rs-mobile-card-value">{system.location}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Type</span>
                  <span className="rs-mobile-card-value">{system.type}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Capacity</span>
                  <span className="rs-mobile-card-value">{system.capacity}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Installed By</span>
                  <span className="rs-mobile-card-value">{system.installedBy}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Last Test</span>
                  <span className="rs-mobile-card-value">{system.lastTestDate}</span>
                </div>
                <div className="rs-mobile-card-field">
                  <span className="rs-mobile-card-label">Next Test</span>
                  <span className="rs-mobile-card-value">{system.nextTestDate}</span>
                </div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(system)}>
                  <FontAwesomeIcon icon={faEye} /> View
                </button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(system)}>
                  <FontAwesomeIcon icon={faClockRotateLeft} /> Test History
                </button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingSystem(system); setShowEditModal(true); }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteSystem(system.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="rs-modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="rs-modal" role="dialog" aria-modal="true" aria-labelledby="create-abseil-title">
            <div className="rs-modal-header">
              <h2 id="create-abseil-title">
                <FontAwesomeIcon icon={faLink} style={{ marginRight: 8, color: 'var(--cs-blue, #1A80E5)' }} />
                Create Abseil System
              </h2>
              <button className="rs-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateSystem(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label htmlFor="abseil-name">System Name *</label>
                    <input
                      id="abseil-name"
                      type="text"
                      value={newSystem.systemName}
                      onChange={e => setNewSystem({ ...newSystem, systemName: e.target.value })}
                      required
                      placeholder="Enter system name"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="abseil-type">Type *</label>
                    <select
                      id="abseil-type"
                      value={newSystem.type}
                      onChange={e => setNewSystem({ ...newSystem, type: e.target.value })}
                      required
                    >
                      {ABSEIL_SYSTEM_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="abseil-location">Location *</label>
                    <input
                      id="abseil-location"
                      type="text"
                      value={newSystem.location}
                      onChange={e => setNewSystem({ ...newSystem, location: e.target.value })}
                      required
                      placeholder="e.g., Roof Zone B"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="abseil-installed-by">Installed By *</label>
                    <input
                      id="abseil-installed-by"
                      type="text"
                      value={newSystem.installedBy}
                      onChange={e => setNewSystem({ ...newSystem, installedBy: e.target.value })}
                      required
                      placeholder="Installer name"
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="abseil-date-installed">Date Installed *</label>
                    <input
                      id="abseil-date-installed"
                      type="date"
                      value={newSystem.dateInstalled}
                      onChange={e => setNewSystem({ ...newSystem, dateInstalled: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="abseil-last-test">Last Test Date *</label>
                    <input
                      id="abseil-last-test"
                      type="date"
                      value={newSystem.lastTestDate}
                      onChange={e => setNewSystem({ ...newSystem, lastTestDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="abseil-next-test">Next Test Date</label>
                    <input
                      id="abseil-next-test"
                      type="date"
                      value={calculateNextTestDate(newSystem.lastTestDate)}
                      readOnly
                    />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="abseil-cert-url">Certificate URL</label>
                    <input
                      id="abseil-cert-url"
                      type="text"
                      value={newSystem.certificateUrl}
                      onChange={e => setNewSystem({ ...newSystem, certificateUrl: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="abseil-notes">Notes</label>
                    <textarea
                      id="abseil-notes"
                      value={newSystem.notes}
                      onChange={e => setNewSystem({ ...newSystem, notes: e.target.value })}
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button
                  type="submit"
                  className="rs-btn rs-btn-primary"
                  disabled={!newSystem.systemName || !newSystem.location || !newSystem.installedBy}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSystem && (
        <div className="rs-modal-overlay" onClick={e => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="rs-modal" role="dialog" aria-modal="true" aria-labelledby="edit-abseil-title">
            <div className="rs-modal-header">
              <h2 id="edit-abseil-title">
                <FontAwesomeIcon icon={faLink} style={{ marginRight: 8, color: 'var(--cs-blue, #1A80E5)' }} />
                Edit Abseil System
              </h2>
              <button className="rs-modal-close" onClick={() => setShowEditModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleEditSystem(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-name">System Name *</label>
                    <input
                      id="edit-abseil-name"
                      type="text"
                      value={editingSystem.systemName}
                      onChange={e => setEditingSystem({ ...editingSystem, systemName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-type">Type *</label>
                    <select
                      id="edit-abseil-type"
                      value={editingSystem.type}
                      onChange={e => setEditingSystem({ ...editingSystem, type: e.target.value })}
                      required
                    >
                      {ABSEIL_SYSTEM_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="edit-abseil-location">Location *</label>
                    <input
                      id="edit-abseil-location"
                      type="text"
                      value={editingSystem.location}
                      onChange={e => setEditingSystem({ ...editingSystem, location: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-installed-by">Installed By *</label>
                    <input
                      id="edit-abseil-installed-by"
                      type="text"
                      value={editingSystem.installedBy}
                      onChange={e => setEditingSystem({ ...editingSystem, installedBy: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-date-installed">Date Installed *</label>
                    <input
                      id="edit-abseil-date-installed"
                      type="date"
                      value={editingSystem.dateInstalled}
                      onChange={e => setEditingSystem({ ...editingSystem, dateInstalled: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-last-test">Last Test Date *</label>
                    <input
                      id="edit-abseil-last-test"
                      type="date"
                      value={editingSystem.lastTestDate}
                      onChange={e => setEditingSystem({ ...editingSystem, lastTestDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="rs-form-group">
                    <label htmlFor="edit-abseil-status">Status</label>
                    <select
                      id="edit-abseil-status"
                      value={editingSystem.status}
                      onChange={e => setEditingSystem({ ...editingSystem, status: e.target.value })}
                    >
                      <option value="Unknown">Unknown</option>
                      <option value="Pass">Pass</option>
                      <option value="Fail">Fail</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="edit-abseil-cert-url">Certificate URL</label>
                    <input
                      id="edit-abseil-cert-url"
                      type="text"
                      value={editingSystem.certificateUrl}
                      onChange={e => setEditingSystem({ ...editingSystem, certificateUrl: e.target.value })}
                    />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label htmlFor="edit-abseil-notes">Notes</label>
                    <textarea
                      id="edit-abseil-notes"
                      value={editingSystem.notes}
                      onChange={e => setEditingSystem({ ...editingSystem, notes: e.target.value })}
                      rows="3"
                    />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-danger" onClick={() => handleDeleteSystem(editingSystem.id)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button
                  type="submit"
                  className="rs-btn rs-btn-primary"
                  disabled={!editingSystem.systemName || !editingSystem.location || !editingSystem.installedBy}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.systemName, location: detailAsset.location } : null}
        assetType="abseil_system"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Capacity', value: detailAsset.capacity },
          { label: 'Installed By', value: detailAsset.installedBy },
          { label: 'Date Installed', value: detailAsset.dateInstalled }
        ] : []}
      />
    </div>
  );
}
